/**
 * The client's Drive folder, on the client's page.
 *
 * Not a file manager. The studio already has one and it is Google Drive, with
 * everybody's desktop sync pointed at it. What was missing was the two minutes
 * spent hunting for which folder belongs to which client, so this is a window
 * onto the folder the studio already recorded, plus the one thing worth doing
 * from here — starting a doc that lands in the right place rather than in
 * somebody's personal drive, which is where every untitled brief currently
 * lives.
 */

import { ChevronRight, ExternalLink, FileText, Folder, Plus, RefreshCw, Image as ImageIcon, FileVideo, FileAudio, FileSpreadsheet, FileCode2, FileArchive, Presentation } from 'lucide-react'
import { useState } from 'react'
import { BentoCard } from '../components/BentoCard'
import { Button } from '../components/Button'
import { useToast } from '../context/ToastContext'
import { api, apiErrorMessage } from '../lib/api'
import { formatDate } from '../lib/format'
import { useApi } from '../useApi'
import {
  driveDay,
  fileKind,
  readableSize,
  whyEmpty,
  type Documents,
  type DriveFile,
} from './documents'
import { motion } from 'framer-motion'

function fileLook(file: DriveFile) {
  const isFolder = file.folder
  if (isFolder) return { Icon: Folder, color: '#94a3b8' }
  const kind = fileKind(file.mimeType)
  
  if (kind === 'Doc' || kind === 'PDF') return { Icon: FileText, color: '#3b82f6' }
  if (kind === 'Sheet') return { Icon: FileSpreadsheet, color: '#10b981' }
  if (kind === 'Slides') return { Icon: Presentation, color: '#f59e0b' }
  if (kind === 'Image') return { Icon: ImageIcon, color: '#ec4899' }
  if (kind === 'Video') return { Icon: FileVideo, color: '#8b5cf6' }
  if (kind === 'Audio') return { Icon: FileAudio, color: '#14b8a6' }
  if (kind === 'Archive') return { Icon: FileArchive, color: '#f43f5e' }
  return { Icon: FileCode2, color: '#64748b' }
}

function DocumentCard({ file, index, onOpenFolder }: { file: DriveFile; index: number; onOpenFolder: (f: DriveFile) => void }) {
  const { Icon, color } = fileLook(file)

  return (
    <motion.a
      href={file.url ?? undefined}
      target="_blank"
      rel="noreferrer noopener"
      // A folder opens here, so its files sit beside the client's other work;
      // a file opens in Drive, which is where it is edited.
      onClick={(e) => {
        if (!file.folder) return
        e.preventDefault()
        onOpenFolder(file)
      }}
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30, delay: Math.min(index * 0.03, 0.3) }}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.97 }}
      className="group relative flex flex-col rounded-2xl border border-shell-border bg-shell p-4 text-left shadow-card transition-shadow hover:shadow-card-hover glass-panel"
    >
      <div className="relative mb-4 flex h-36 items-center justify-center">
        <span
          aria-hidden
          className="absolute h-28 w-24 -rotate-6 rounded-xl border border-shell-border bg-shell-border/40 transition-transform duration-300 group-hover:-rotate-12 group-hover:-translate-x-2 group-active:-rotate-12 group-active:-translate-x-2"
          style={{ left: '50%', marginLeft: -60 }}
        />
        <span
          aria-hidden
          className="absolute h-28 w-24 rotate-6 rounded-xl border border-shell-border bg-shell-border/25 transition-transform duration-300 group-hover:rotate-12 group-hover:translate-x-2 group-active:rotate-12 group-active:translate-x-2"
          style={{ left: '50%', marginLeft: 12 }}
        />
        <div
          className="relative flex h-32 w-28 items-center justify-center overflow-hidden rounded-xl border shadow-sm"
          style={{ borderColor: `${color}40`, backgroundColor: `${color}12` }}
        >
          <Icon size={42} style={{ color }} strokeWidth={1.5} />
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-end">
        <h4 className="line-clamp-2 text-sm font-semibold text-shell-text-active leading-tight group-hover:text-brand-400">
          {file.name}
        </h4>
        <div className="mt-2 flex items-center justify-between text-xs text-shell-text">
          <span>{fileKind(file.mimeType)}</span>
          {readableSize(file.size) !== '' && <span>{readableSize(file.size)}</span>}
        </div>
      </div>
    </motion.a>
  )
}

export function ClientDocuments({ clientId }: { clientId: string }) {
  const [trail, setTrail] = useState<{ id: string; name: string }[]>([])
  const [search, setSearch] = useState('')
  const { result, reload } = useApi<Documents>(`/clients/${clientId}/documents`, {
    query: { trail: trail.length === 0 ? undefined : trail.map((t) => t.id).join(',') }
  })
  const { showToast } = useToast()
  const [naming, setNaming] = useState(false)
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)

  const data = result.data
  const failure = result.state === 'failed' ? result.error.message : null

  async function create(event: React.FormEvent) {
    event.preventDefault()
    const name = title.trim()
    if (name === '' || busy) return

    setBusy(true)
    try {
      const made = await api.post<DriveFile>(`/clients/${clientId}/documents`, { title: name })
      setTitle('')
      setNaming(false)
      showToast(`"${made.name}" is in the client folder.`)
      reload()
    } catch (err) {
      showToast(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const canCreate = data !== null && data.connected && data.folder !== null && trail.length === 0
  const q = search.trim().toLowerCase()
  const shown = data === null ? [] : q === '' ? data.files : data.files.filter((f) => f.name.toLowerCase().includes(q))
  const openFolder = (f: DriveFile) => {
    setSearch('')
    setTrail((t) => [...t, { id: f.id, name: f.name }])
  }

  return (
    <div className="mb-8">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-shell-text-active">Documents</h2>
          <p className="text-sm text-shell-text">Their folder in Drive</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.folder?.url != null && (
            <a
              href={data.folder.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex h-9 items-center gap-2 rounded-xl border border-shell-border bg-shell-border/30 px-3 text-sm font-medium text-shell-text transition-colors hover:bg-shell-border/60 hover:text-shell-text-active"
            >
              <ExternalLink size={15} /> Open Drive
            </a>
          )}
          <button
            type="button"
            onClick={reload}
            aria-label="Refresh"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-shell-border bg-shell-border/30 text-shell-text transition-colors hover:bg-shell-border/60 hover:text-shell-text-active"
          >
            <RefreshCw size={15} className={result.state === 'loading' ? 'animate-spin' : ''} />
          </button>
          {canCreate && !naming && (
            <button 
              onClick={() => setNaming(true)}
              className="inline-flex h-9 items-center gap-2 rounded-xl bg-brand-500 px-3 text-sm font-medium text-white transition-colors hover:bg-brand-600"
            >
              <Plus size={15} /> New doc
            </button>
          )}
        </div>
      </div>

      {naming && (
        <form onSubmit={create} className="mb-6 flex flex-wrap items-center gap-2 rounded-xl border border-shell-border bg-shell p-3 shadow-sm">
          <input
            autoFocus
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="October reel scripts"
            aria-label="Document name"
            className="field-input min-w-[200px] flex-1"
          />
          <button type="submit" disabled={busy || title.trim() === ''} className="inline-flex h-10 items-center justify-center rounded-xl bg-brand-500 px-4 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50">
            {busy ? 'Creating…' : 'Create'}
          </button>
          <button
            type="button"
            onClick={() => {
              setNaming(false)
              setTitle('')
            }}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-shell-border px-4 text-sm font-medium text-shell-text transition-colors hover:bg-shell-border/60"
          >
            Cancel
          </button>
        </form>
      )}

      {failure !== null && (
        <p
          role="alert"
          className="mb-6 rounded-xl border border-danger-500/20 bg-danger-50 px-4 py-3 text-sm text-danger-600"
        >
          {failure}
        </p>
      )}

      {failure === null && data === null && (
        <p className="py-12 text-center text-sm text-shell-text/50">Reading the folder…</p>
      )}

      {(trail.length > 0 || (data !== null && data.files.length > 0)) && (
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <nav aria-label="Folder" className="flex min-w-0 flex-wrap items-center gap-1 text-sm">
            <button
              type="button"
              onClick={() => {
                setSearch('')
                setTrail([])
              }}
              className={`rounded-lg px-2 py-1 font-medium hover:bg-shell-border/40 ${trail.length === 0 ? 'text-shell-text-active' : 'text-shell-text'}`}
            >
              {data?.folder?.label ?? 'Client folder'}
            </button>
            {trail.map((t, i) => (
              <span key={t.id} className="flex min-w-0 items-center gap-1">
                <ChevronRight size={14} className="shrink-0 text-shell-text/50" />
                <button
                  type="button"
                  onClick={() => {
                    setSearch('')
                    setTrail((all) => all.slice(0, i + 1))
                  }}
                  className={`truncate rounded-lg px-2 py-1 font-medium hover:bg-shell-border/40 ${i === trail.length - 1 ? 'text-shell-text-active' : 'text-shell-text'}`}
                >
                  {t.name}
                </button>
              </span>
            ))}
          </nav>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search this folder"
            aria-label="Search this folder"
            className="field-input w-full sm:w-56"
          />
        </div>
      )}

      {failure === null && data !== null && (
        <div className="documents-canvas rounded-card border border-shell-border p-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {shown.map((f, i) => (
              <DocumentCard key={f.id} file={f} index={i} onOpenFolder={openFolder} />
            ))}
          </div>
          {whyEmpty(data) !== null && trail.length === 0 && (
            <p className="py-12 text-center text-sm text-shell-text/50">{whyEmpty(data)}</p>
          )}
          {data.files.length === 0 && trail.length > 0 && (
            <p className="py-12 text-center text-sm text-shell-text/50">This folder is empty.</p>
          )}
          {data.files.length > 0 && shown.length === 0 && (
            <p className="py-12 text-center text-sm text-shell-text/50">Nothing here is called &ldquo;{search.trim()}&rdquo;.</p>
          )}
        </div>
      )}
    </div>
  )
}
